import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, Chip, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Checkbox, FormControlLabel } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { pageTypeColors } from "@/theme/colors";
import { useGetAdminTasksQuery, useUpdateTaskStatusMutation, useUpdateWebsiteTaskMutation } from "@/src/services/api";

/* ── helpers ────────────────────────────────────────────────────────────────── */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function readTodoItems() {
  try {
    const raw = localStorage.getItem("todoListItems");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function writeTodoItems(items) {
  localStorage.setItem("todoListItems", JSON.stringify(items));
  window.dispatchEvent(new Event("todoListChanged"));
}
function inferPageType(pagePath) {
  const p = (pagePath || "").toLowerCase();
  if (p.startsWith("/organizations")) return "organization";
  if (p === "/" || p.startsWith("/home")) return "homepage";
  if (p.startsWith("/events")) return "event";
  if (p.startsWith("/regulations")) return "regulation";
  if (p.startsWith("/articles")) return "article";
  if (p.startsWith("/admin")) return "admin";
  return "organization";
}
const PAGE_TYPE_PREFIXES = {
  organization: "Organization",
  event: "Event",
  regulation: "Regulation",
  article: "Article",
  admin: "Admin",
  homepage: "Homepage"
};
function stripPagePrefix(label, pagePath) {
  const prefix = PAGE_TYPE_PREFIXES[inferPageType(pagePath)];
  if (prefix && label.startsWith(prefix + " ")) {
    return label.slice(prefix.length + 1);
  }
  const plural = prefix + "s";
  if (prefix && label.startsWith(plural + " ")) {
    return label.slice(plural.length + 1);
  }
  return label;
}
const PRIORITY_COLORS = {
  0: "#7f1d1d",
  1: "#d32f2f",
  2: "#ed6c02",
  3: "#2e7d32"
};
const PRIORITY_LABELS = {
  0: "P0",
  1: "P1",
  2: "P2",
  3: "P3"
};

/* ── component ──────────────────────────────────────────────────────────────── */

export default function TodoList({
  open,
  onClose
}) {
  const [entries, setEntries] = useState(readTodoItems);
  const [confirmId, setConfirmId] = useState(null);

  // Task detail modal state
  const [detailTask, setDetailTask] = useState(null);
  const [localSubtasks, setLocalSubtasks] = useState([]);
  const [subtasksDirty, setSubtasksDirty] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [subtaskSaving, setSubtaskSaving] = useState(false);
  const {
    data: allTasks
  } = useGetAdminTasksQuery(undefined, {
    skip: !open || entries.length === 0
  });
  const [updateStatus] = useUpdateTaskStatusMutation();
  const [updateTask] = useUpdateWebsiteTaskMutation();

  // Listen for external changes (e.g. + button on task card)
  useEffect(() => {
    const handler = () => setEntries(readTodoItems());
    window.addEventListener("todoListChanged", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("todoListChanged", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  // Build a lookup map for quick task data access
  const taskMap = React.useMemo(() => {
    const map = new Map();
    if (allTasks) {
      for (const t of allTasks) map.set(t.id, t);
    }
    return map;
  }, [allTasks]);
  const hasResolved = entries.some(e => e.resolved);
  const handleResolve = useCallback(async taskId => {
    try {
      await updateStatus({
        id: taskId,
        status: "RESOLVED"
      }).unwrap();
      setEntries(prev => {
        const next = prev.map(e => e.id === taskId ? {
          ...e,
          resolved: true
        } : e);
        writeTodoItems(next);
        return next;
      });
    } catch {
      // silently fail — task may already be resolved
    }
    setConfirmId(null);
  }, [updateStatus]);
  const handleClearResolved = useCallback(() => {
    setEntries(prev => {
      const next = prev.filter(e => !e.resolved);
      writeTodoItems(next);
      return next;
    });
  }, []);
  const handleRemove = useCallback(taskId => {
    setEntries(prev => {
      const next = prev.filter(e => e.id !== taskId);
      writeTodoItems(next);
      return next;
    });
  }, []);
  const handleOpenDetail = useCallback(task => {
    setDetailTask(task);
    setLocalSubtasks(task.subtasks.map(st => ({
      ...st
    })));
    setSubtasksDirty(false);
  }, []);
  const handleToggleSubtask = useCallback(id => {
    setLocalSubtasks(prev => prev.map(st => st.id === id ? {
      ...st,
      completed: !st.completed
    } : st));
    setSubtasksDirty(true);
  }, []);
  const handleSaveSubtasks = useCallback(async () => {
    if (!detailTask) return;
    setSubtaskSaving(true);
    try {
      await updateTask({
        id: detailTask.id,
        subtasks: localSubtasks.map(st => ({
          id: st.id,
          text: st.text,
          completed: st.completed
        }))
      }).unwrap();
      setSubtasksDirty(false);
      setDetailTask(null);
    } catch {
      // stay open on error
    } finally {
      setSubtaskSaving(false);
    }
  }, [detailTask, localSubtasks, updateTask]);
  const handleCloseDetail = useCallback(() => {
    if (subtasksDirty) {
      setConfirmCloseOpen(true);
    } else {
      setDetailTask(null);
    }
  }, [subtasksDirty]);
  const handleDiscardAndClose = useCallback(() => {
    setConfirmCloseOpen(false);
    setSubtasksDirty(false);
    setDetailTask(null);
  }, []);
  if (!open) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Box, {
      sx: {
        position: "fixed",
        bottom: 24,
        right: 24,
        width: 320,
        height: 550,
        bgcolor: "background.paper",
        border: "3px solid #2196f3",
        borderRadius: 2,
        boxShadow: 8,
        display: "flex",
        flexDirection: "column",
        zIndex: 1300
      },
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0
        },
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "subtitle1",
          fontWeight: 700,
          children: "To-do List"
        }), /*#__PURE__*/_jsx(Typography, {
          component: "a",
          href: "/Admin?tab=tasks",
          variant: "body2",
          sx: {
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 600,
            "&:hover": {
              textDecoration: "underline"
            }
          },
          children: "Task Manager"
        }), /*#__PURE__*/_jsx(IconButton, {
          size: "small",
          onClick: onClose,
          children: /*#__PURE__*/_jsx(CloseIcon, {
            fontSize: "small"
          })
        })]
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          flex: 1,
          overflowY: "auto",
          px: 1,
          py: 0.5
        },
        children: [entries.length === 0 && /*#__PURE__*/_jsx(Typography, {
          variant: "body2",
          color: "text.secondary",
          sx: {
            textAlign: "center",
            py: 4
          },
          children: "No items yet. Use the + button on task cards to add."
        }), entries.map(entry => {
          const task = taskMap.get(entry.id);
          if (!task) {
            return /*#__PURE__*/_jsx(Box, {
              sx: {
                p: 1,
                mb: 0.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                opacity: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              },
              children: /*#__PURE__*/_jsx(Typography, {
                variant: "caption",
                color: "text.secondary",
                children: "Loading\u2026"
              })
            }, entry.id);
          }
          const pageType = inferPageType(task.pagePath);
          const pageColor = pageTypeColors[pageType] || "#757575";
          const priorityColor = PRIORITY_COLORS[task.priority] || "#757575";
          const bodyPreview = task.body?.slice(0, 80) || task.messages?.[0]?.body?.slice(0, 80) || "(no description)";
          return /*#__PURE__*/_jsxs(Box, {
            onClick: () => handleOpenDetail(task),
            sx: {
              p: 1,
              mb: 0.5,
              border: 1,
              borderColor: entry.resolved ? "success.light" : "#9e9e9e",
              borderRadius: 1,
              opacity: entry.resolved ? 0.5 : 1,
              bgcolor: entry.resolved ? "action.hover" : "background.paper",
              cursor: "pointer",
              "&:hover": {
                bgcolor: "action.hover"
              }
            },
            children: [/*#__PURE__*/_jsxs(Box, {
              sx: {
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 0.5
              },
              children: [/*#__PURE__*/_jsx(Chip, {
                label: pageType.charAt(0).toUpperCase() + pageType.slice(1),
                size: "small",
                sx: {
                  fontSize: "0.65rem",
                  height: 18,
                  bgcolor: "transparent",
                  border: "1px solid",
                  borderColor: pageColor,
                  color: pageColor
                }
              }), /*#__PURE__*/_jsx(Chip, {
                label: PRIORITY_LABELS[task.priority] || "P?",
                size: "small",
                sx: {
                  fontSize: "0.65rem",
                  height: 18,
                  bgcolor: priorityColor,
                  color: "#fff"
                }
              }), task.label && /*#__PURE__*/_jsx(Chip, {
                label: stripPagePrefix(task.label, task.pagePath),
                size: "small",
                sx: {
                  fontSize: "0.65rem",
                  height: 18,
                  bgcolor: "action.selected"
                }
              })]
            }), /*#__PURE__*/_jsx(Typography, {
              variant: "body2",
              sx: {
                fontSize: "0.75rem",
                lineHeight: 1.3,
                mb: 0.5,
                textDecoration: entry.resolved ? "line-through" : "none",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              },
              children: bodyPreview
            }), !entry.resolved && /*#__PURE__*/_jsxs(Box, {
              sx: {
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.5
              },
              children: [/*#__PURE__*/_jsx(Button, {
                size: "small",
                variant: "text",
                color: "error",
                onClick: e => {
                  e.stopPropagation();
                  handleRemove(entry.id);
                },
                sx: {
                  fontSize: "0.7rem",
                  minWidth: 0,
                  textTransform: "none",
                  px: 1,
                  py: 0
                },
                children: "Remove"
              }), /*#__PURE__*/_jsx(Button, {
                size: "small",
                variant: "outlined",
                color: "success",
                startIcon: /*#__PURE__*/_jsx(CheckCircleOutlineIcon, {
                  sx: {
                    fontSize: 14
                  }
                }),
                onClick: e => {
                  e.stopPropagation();
                  setConfirmId(entry.id);
                },
                sx: {
                  fontSize: "0.7rem",
                  textTransform: "none",
                  py: 0
                },
                children: "Resolve"
              })]
            })]
          }, entry.id);
        })]
      }), hasResolved && /*#__PURE__*/_jsx(Box, {
        sx: {
          px: 2,
          py: 1,
          borderTop: 1,
          borderColor: "divider",
          flexShrink: 0
        },
        children: /*#__PURE__*/_jsx(Button, {
          size: "small",
          fullWidth: true,
          variant: "outlined",
          onClick: handleClearResolved,
          sx: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.75rem"
          },
          children: "Clear Resolved"
        })
      })]
    }), /*#__PURE__*/_jsxs(Dialog, {
      open: confirmId !== null,
      onClose: () => setConfirmId(null),
      onKeyDown: e => {
        if (e.key === "Enter" && confirmId) {
          e.preventDefault();
          handleResolve(confirmId);
        }
      },
      children: [/*#__PURE__*/_jsx(DialogTitle, {
        children: "Resolve Task"
      }), /*#__PURE__*/_jsx(DialogContent, {
        children: /*#__PURE__*/_jsx(DialogContentText, {
          children: "Mark this task as resolved? This will update the task status in the backend."
        })
      }), /*#__PURE__*/_jsxs(DialogActions, {
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: () => setConfirmId(null),
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          variant: "contained",
          color: "success",
          onClick: () => confirmId && handleResolve(confirmId),
          children: "Resolve"
        })]
      })]
    }), /*#__PURE__*/_jsx(Dialog, {
      open: detailTask !== null,
      onClose: handleCloseDetail,
      maxWidth: "sm",
      fullWidth: true,
      children: detailTask && (() => {
        const pageType = inferPageType(detailTask.pagePath);
        const pageColor = pageTypeColors[pageType] || "#757575";
        const priorityColor = PRIORITY_COLORS[detailTask.priority] || "#757575";
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(DialogTitle, {
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 1,
              pr: 6
            },
            children: [/*#__PURE__*/_jsx(Chip, {
              label: pageType.charAt(0).toUpperCase() + pageType.slice(1),
              size: "small",
              sx: {
                fontSize: "0.7rem",
                height: 20,
                bgcolor: "transparent",
                border: "1px solid",
                borderColor: pageColor,
                color: pageColor
              }
            }), /*#__PURE__*/_jsx(Chip, {
              label: PRIORITY_LABELS[detailTask.priority] || "P?",
              size: "small",
              sx: {
                fontSize: "0.7rem",
                height: 20,
                bgcolor: priorityColor,
                color: "#fff"
              }
            }), detailTask.label && /*#__PURE__*/_jsx(Typography, {
              variant: "subtitle1",
              fontWeight: 600,
              sx: {
                ml: 0.5
              },
              children: stripPagePrefix(detailTask.label, detailTask.pagePath)
            }), /*#__PURE__*/_jsx(IconButton, {
              size: "small",
              onClick: handleCloseDetail,
              sx: {
                position: "absolute",
                right: 8,
                top: 8
              },
              children: /*#__PURE__*/_jsx(CloseIcon, {
                fontSize: "small"
              })
            })]
          }), /*#__PURE__*/_jsxs(DialogContent, {
            dividers: true,
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "caption",
              color: "text.secondary",
              gutterBottom: true,
              children: detailTask.pagePath
            }), /*#__PURE__*/_jsx(Typography, {
              variant: "body2",
              sx: {
                mt: 1,
                mb: 2,
                whiteSpace: "pre-wrap"
              },
              children: detailTask.body || detailTask.messages?.[0]?.body || "(no description)"
            }), localSubtasks.length > 0 && /*#__PURE__*/_jsxs(Box, {
              children: [/*#__PURE__*/_jsx(Typography, {
                variant: "subtitle2",
                fontWeight: 700,
                sx: {
                  mb: 0.5
                },
                children: "Subtasks"
              }), localSubtasks.map(st => /*#__PURE__*/_jsx(FormControlLabel, {
                control: /*#__PURE__*/_jsx(Checkbox, {
                  checked: st.completed,
                  onChange: () => handleToggleSubtask(st.id),
                  size: "small"
                }),
                label: /*#__PURE__*/_jsx(Typography, {
                  variant: "body2",
                  sx: {
                    textDecoration: st.completed ? "line-through" : "none",
                    color: st.completed ? "text.secondary" : "text.primary"
                  },
                  children: st.text
                }),
                sx: {
                  display: "flex",
                  mb: 0
                }
              }, st.id))]
            })]
          }), /*#__PURE__*/_jsxs(DialogActions, {
            children: [/*#__PURE__*/_jsx(Button, {
              onClick: handleCloseDetail,
              children: subtasksDirty ? "Cancel" : "Close"
            }), subtasksDirty && /*#__PURE__*/_jsx(Button, {
              variant: "contained",
              disabled: subtaskSaving,
              onClick: handleSaveSubtasks,
              children: subtaskSaving ? "Saving…" : "Save"
            })]
          })]
        });
      })()
    }), /*#__PURE__*/_jsxs(Dialog, {
      open: confirmCloseOpen,
      onClose: () => setConfirmCloseOpen(false),
      onKeyDown: e => {
        if (e.key === "Enter") {
          e.preventDefault();
          void handleSaveSubtasks();
          setConfirmCloseOpen(false);
        }
      },
      children: [/*#__PURE__*/_jsx(DialogTitle, {
        children: "Unsaved Changes"
      }), /*#__PURE__*/_jsx(DialogContent, {
        children: /*#__PURE__*/_jsx(DialogContentText, {
          children: "You have unsaved subtask changes. Discard them?"
        })
      }), /*#__PURE__*/_jsxs(DialogActions, {
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: () => setConfirmCloseOpen(false),
          children: "Keep Editing"
        }), /*#__PURE__*/_jsx(Button, {
          variant: "contained",
          color: "warning",
          onClick: handleDiscardAndClose,
          children: "Discard"
        }), /*#__PURE__*/_jsx(Button, {
          variant: "contained",
          disabled: subtaskSaving,
          onClick: async () => {
            await handleSaveSubtasks();
            setConfirmCloseOpen(false);
          },
          children: subtaskSaving ? "Saving…" : "Save & Close"
        })]
      })]
    })]
  });
}